import React from 'react';
import * as PropTypes from "prop-types";
import {API_URL} from "../../../constants";
import {makeRequest} from "../../../services/requestService";
import {
    Box,
    Checkbox,
    Chip,
    Collapse, Dialog, DialogActions, DialogContent, DialogTitle, Divider,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow, TextField, Typography, useTheme
} from "@mui/material";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {useTranslation} from "react-i18next";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CreateTagsCollection from "../../profile/Annotation/CreateTagsCollection";
import {TagUpdaterComponent} from "../../profile/Annotation/TagEditorComponent";

const ITEMS_PER_PAGE = 10;

async function fetchLabelSet(set) {
    const url = `${API_URL}/label/set/${set}`;

    const response = await makeRequest(url);

    if (response.status !== 200) {
        throw response;
    }

    return response.data;
}

async function deleteLabelSet(id) {
    const url = `${API_URL}/label/set/${id}`;
    const response = await makeRequest(url, "DELETE");

    if (response.status !== 200) {
        throw response;
    }
    return response.data;
}

async function deleteLabel(id) {
    const url = `${API_URL}/label/${id}`;
    const response = await makeRequest(url, "DELETE");

    if (response.status !== 200) {
        throw response;
    }
    return response.data;
}

async function fetchLabelSets(labelSets, page) {
    return (await Promise.all(labelSets.map(async set => {
        try {
            return await fetchLabelSet(set, page)
        } catch (error) {
            console.warn(error);
        }
    }))).filter(set => set);
}

function LabelRow(props) {
    const {t} = useTranslation('common');
    const theme = useTheme();
    const {label, onEditLabelClick, onDelete} = props;

    async function handleDelete() {
        await deleteLabel(label.id);
        onDelete();
    }

    return (
        <TableRow key={label.id}>
            <TableCell component="th" scope="row">{label.id}</TableCell>
            <TableCell>
                {label.label}
            </TableCell>
            <TableCell>
                {label.description}
            </TableCell>
            <TableCell>
                {label.url}
            </TableCell>
            <TableCell>
                {label.type}
            </TableCell>
            <TableCell>
                {label.altoType}
            </TableCell>
            <TableCell>
                <Chip label={label.color}
                      sx={{
                          backgroundColor: label.color,
                          color: theme.palette.getContrastText(label.color),
                      }}
                /></TableCell>
            <TableCell align="right">
                <IconButton onClick={() => props.onEditLabelClick(label)}>
                    <EditIcon/>
                </IconButton>
                <IconButton onClick={() => handleDelete()}>
                    <DeleteIcon color="error"/>
                </IconButton>
            </TableCell>
        </TableRow>
    )
}

function LabelTable(props) {
    const {t} = useTranslation('common');

    return (
        <TableContainer component={Paper}>
            <Table size="small">
                <TableHead>
                    <TableCell>
                        ID
                    </TableCell>
                    <TableCell>
                        {t('main.pages.admin.user.tabs.label_set.table.label.label')}
                    </TableCell>
                    <TableCell>
                        {t('main.pages.admin.user.tabs.label_set.table.label.description')}
                    </TableCell>
                    <TableCell>
                        {t('main.pages.admin.user.tabs.label_set.table.label.url')}
                    </TableCell>
                    <TableCell>
                        {t('main.pages.admin.user.tabs.label_set.table.label.type')}
                    </TableCell>
                    <TableCell>
                        {t('main.pages.admin.user.tabs.label_set.table.label.alto')}
                    </TableCell>
                    <TableCell>
                        {t('main.pages.admin.user.tabs.label_set.table.label.color')}
                    </TableCell>
                    <TableCell></TableCell>
                </TableHead>
                <TableBody>
                    {props.labels.map(label => <LabelRow label={label} onEditLabelClick={props.onEditLabelClick}
                                                         onDelete={props.onDelete}/>)}
                </TableBody>
            </Table>
        </TableContainer>
    )
}

function SetRow(props) {
    const {t} = useTranslation("common");
    const {set, open, onOpen, onEditSetClick, onEditLabelClick, onDelete} = props;

    async function handleDelete() {
        await deleteLabelSet(set.id);
        onDelete();
    }

    return (
        <>
            <TableRow sx={{'& > *': {boderBottom: 'unset'}}}>
                <TableCell>
                    <IconButton onClick={() => onOpen(!open)}>
                        {open ? <KeyboardArrowDownIcon/> : <KeyboardArrowRightIcon/>}
                    </IconButton>
                </TableCell>
                <TableCell>
                    {set.name}
                </TableCell>
                <TableCell>
                    {set.description}
                </TableCell>
                <TableCell>
                    <Checkbox checked={set.private} readOnly/>
                </TableCell>
                <TableCell align="right">
                    <IconButton onClick={() => onEditSetClick(set)}>
                        <EditIcon/>
                    </IconButton>
                    <IconButton onClick={() => handleDelete()}>
                        <DeleteIcon color="error"/>
                    </IconButton>
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell sx={{paddingY: 0}} colSpan={5}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box p={1}>
                            <Typography variant="h6" gutterBottom component="div">
                                {t('main.pages.admin.user.tabs.label_set.table.label.title')}
                            </Typography>
                            <LabelTable labels={set.labels} onEditLabelClick={onEditLabelClick} onDelete={onDelete}/>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    )
}

export default function LabelsTab(props) {
    const {t} = useTranslation("common");
    const [sets, setSets] = React.useState([]);
    const [openSet, setOpenSet] = React.useState(-1);
    const [editingSet, setEditingSet] = React.useState();
    const [editingLabel, setEditingLabel] = React.useState();
    const [updateSets, setUpdateSet] = React.useState(0);

    React.useEffect(async () => {
        const sets = await fetchLabelSets(props.user.labels, 0);
        setSets(sets);
    }, [props.user, updateSets]);

    function handleOpen(set, label) {
        setEditingLabel(label);
        setEditingSet(set);
    }

    function handleDelete() {
        setUpdateSet(updateSets + 1);
    }

    return (
        <Box>
            <Typography variant="h5" mb={2}>
                {t('main.pages.admin.user.tabs.label_set.title')}
            </Typography>
            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell/>
                            <TableCell>
                                {t('main.pages.admin.user.tabs.label_set.table.name')}
                            </TableCell>
                            <TableCell>
                                {t('main.pages.admin.user.tabs.label_set.table.description')}
                            </TableCell>
                            <TableCell>
                                {t('main.pages.admin.user.tabs.label_set.table.private')}
                            </TableCell>
                            <TableCell/>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sets.map(set => (
                            <SetRow key={set.id} set={set}
                                    open={openSet === set.id}
                                    onDelete={handleDelete}
                                    onOpen={(open) => setOpenSet(open ? set.id : -1)}
                                    onEditSetClick={(set) => handleOpen(set, null)}
                                    onEditLabelClick={(label) => handleOpen(null, label)}
                            />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <Dialog open={Boolean(editingSet) || Boolean(editingLabel)} onClose={() => handleOpen(null, null)}>
                <DialogTitle>
                    {Boolean(editingSet) && t('main.pages.admin.user.tabs.label_set.dialog.set')}
                    {Boolean(editingLabel) && t('main.pages.admin.user.tabs.label_set.dialog.label')}
                </DialogTitle>
                <Divider/>
                <DialogContent>
                    {editingSet && <CreateTagsCollection
                        id={editingSet.id}
                        onSubmit={() => {
                            setUpdateSet(updateSets + 1);
                            handleOpen(null, null);
                        }}
                        mode={"EDIT"}
                        defaultValue={editingSet}
                    />}
                    {editingLabel && <TagUpdaterComponent
                        label={editingLabel}
                        onSubmit={() => {
                            setUpdateSet(updateSets + 1);
                            handleOpen(null, null);
                        }}
                    />}
                </DialogContent>
            </Dialog>
        </Box>
    )
}

LabelsTab.propTypes = {
    user: PropTypes.object.isRequired,
}